<template>
  <div class="my-ads-page">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <button class="back-btn" @click="$router.back()" title="뒤로가기">←</button>
      <h1 class="page-title">내 광고</h1>
      <button class="new-btn" @click="goNewApply">+ 신규 광고</button>
    </div>

    <div v-if="loading" class="state-block">
      <div class="spinner"></div>
      <p class="state-desc">광고 목록을 불러오는 중...</p>
    </div>

    <div v-else-if="errorMessage" class="state-block">
      <p class="state-error">{{ errorMessage }}</p>
      <button class="primary-btn" @click="fetchCampaigns">다시 시도</button>
    </div>

    <div v-else-if="campaigns.length === 0" class="empty-state">
      <div class="empty-icon">📢</div>
      <p class="empty-title">아직 신청한 광고가 없습니다</p>
      <p class="empty-desc">홈 하단·상세 페이지에 노출되는 광고를 직접 신청해보세요.</p>
      <button class="primary-btn" @click="goNewApply">광고 신청하기</button>
    </div>

    <div v-else class="campaign-list">
      <div
        v-for="campaign in campaigns"
        :key="campaign.id"
        class="campaign-card"
        :class="{ clickable: !isDraft(campaign) }"
        @click="onCardClick(campaign)"
      >
        <div class="thumb-wrapper">
          <img v-if="campaign.material?.imageUrl" :src="campaign.material.imageUrl" :alt="campaign.material?.title || ''" class="thumb" />
          <div v-else class="thumb thumb-placeholder">이미지 없음</div>
        </div>

        <div class="info">
          <div class="info-top">
            <h3 class="campaign-title">{{ campaign.material?.title || "(제목 없음)" }}</h3>
            <span class="status-badge" :class="`status-${campaign.status}`">{{ statusLabel(campaign.status) }}</span>
          </div>

          <div class="plan-line">
            {{ planLabel(campaign) }}
            <span v-if="campaign.lastPaymentStatus === 'failed'" class="warn-text">결제 실패</span>
          </div>

          <div v-if="!isDraft(campaign)" class="meta-line">
            <span>다음 결제 {{ formatDate(campaign.nextBillingAt) || "-" }}</span>
            <span class="dot">·</span>
            <span>{{ formatWon(campaign.planSnapshot?.totalAmount) }}/월</span>
          </div>

          <div v-else class="meta-line">
            <span>임시저장 · 마지막 수정 {{ formatDate(campaign.updatedAt) || "-" }}</span>
          </div>
        </div>

        <div class="actions" @click.stop>
          <button v-if="isDraft(campaign)" class="action-primary" @click="goEdit(campaign.id)">이어서 작성</button>
          <button v-else class="action-secondary" @click="goDetail(campaign.id)">성과 상세 ›</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listMyAdCampaigns, formatWon } from "@/utils/adApplication";

const STATUS_LABELS = {
  draft: "임시저장",
  pending_payment: "결제 진행 중",
  pending_review: "심사 대기",
  active: "노출 중",
  paused: "일시정지",
  rejected: "반려",
  canceled: "해지 예정",
  expired: "종료",
};

export default {
  name: "MyAds",
  data() {
    return {
      loading: true,
      errorMessage: "",
      campaigns: [],
    };
  },
  mounted() {
    this.fetchCampaigns();
  },
  methods: {
    formatWon,
    async fetchCampaigns() {
      try {
        this.loading = true;
        this.errorMessage = "";
        const result = await listMyAdCampaigns();
        this.campaigns = Array.isArray(result) ? result : [];
      } catch (e) {
        console.error("내 광고 목록 조회 실패", e);
        this.errorMessage = e?.response?.data?.message || e?.message || "광고 목록을 불러오지 못했습니다.";
      } finally {
        this.loading = false;
      }
    },
    isDraft(campaign) {
      return !campaign?.status || campaign.status === "draft";
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || "임시저장";
    },
    planLabel(campaign) {
      const name = campaign?.planSnapshot?.name;
      if (!name) return "플랜 미선택";
      return name;
    },
    formatDate(value) {
      if (!value) return "";
      const raw = value._seconds ? new Date(value._seconds * 1000) : new Date(value);
      if (Number.isNaN(raw.getTime())) return "";
      return `${raw.getFullYear()}.${String(raw.getMonth() + 1).padStart(2, "0")}.${String(raw.getDate()).padStart(2, "0")}`;
    },
    goNewApply() {
      this.$router.push("/ads/apply");
    },
    goEdit(id) {
      this.$router.push(`/ads/apply/${id}`);
    },
    goDetail(id) {
      this.$router.push(`/my-ads/${id}`);
    },
    onCardClick(campaign) {
      if (this.isDraft(campaign)) {
        this.goEdit(campaign.id);
      } else {
        this.goDetail(campaign.id);
      }
    },
  },
};
</script>

<style scoped>
.my-ads-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 18px 80px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.back-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  color: #495057;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.back-btn:hover {
  background: #f1f3f5;
}

.page-title {
  flex: 1;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 20px;
  color: #2d3748;
  margin: 0;
}

.new-btn {
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  cursor: pointer;
}

.new-btn:hover {
  background: #4da3ff;
}

/* 상태 블록 */
.state-block {
  text-align: center;
  padding: 80px 20px;
}

.spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #e2e8f0;
  border-top-color: #66bdff;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.state-desc { color: #4a5568; font-size: 14px; }
.state-error { color: #c53030; font-size: 14px; margin-bottom: 16px; }

/* Empty */
.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-title { font-family: "NanumSquareRoundEB", sans-serif; font-size: 18px; color: #2d3748; margin: 0 0 6px; }
.empty-desc { font-size: 13px; color: #6c757d; margin: 0 0 18px; }

/* Campaign card */
.campaign-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.campaign-card {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  align-items: center;
  transition: box-shadow 0.15s;
}

.campaign-card.clickable {
  cursor: pointer;
}

.campaign-card.clickable:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.thumb-wrapper {
  width: 120px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-placeholder {
  color: #adb5bd;
  font-size: 12px;
}

.info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.info-top {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.campaign-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 16px;
  color: #2d3748;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.status-badge {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 11px;
}

.status-draft { background: #f1f3f5; color: #495057; }
.status-pending_payment { background: #fff3bf; color: #5c3c00; }
.status-pending_review { background: #fff3bf; color: #5c3c00; }
.status-active { background: #d3f9d8; color: #2b8a3e; }
.status-paused { background: #ffe3e3; color: #c92a2a; }
.status-rejected { background: #ffe3e3; color: #c92a2a; }
.status-canceled { background: #e9ecef; color: #495057; }
.status-expired { background: #e9ecef; color: #495057; }

.plan-line {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.warn-text {
  background: #ffe3e3;
  color: #c92a2a;
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 999px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.meta-line {
  font-size: 12px;
  color: #868e96;
  display: flex;
  align-items: center;
  gap: 6px;
}

.dot {
  color: #ced4da;
}

.actions {
  display: flex;
  gap: 8px;
}

.action-primary {
  background: #66bdff;
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  cursor: pointer;
}

.action-primary:hover { background: #4da3ff; }

.action-secondary {
  background: #fff;
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 8px 14px;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 12px;
  cursor: pointer;
}

.action-secondary:hover { background: #f8f9fa; }

.primary-btn {
  background: #66bdff;
  color: #fff;
  border: none;
  padding: 12px 22px;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.primary-btn:hover { background: #4da3ff; }

@media (max-width: 720px) {
  .campaign-card {
    grid-template-columns: 80px 1fr;
    grid-template-rows: auto auto;
  }
  .actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
  .thumb-wrapper {
    width: 80px;
    height: 60px;
  }
}
</style>
